"use client";

import Link from "next/link";
import { Cookie } from "lucide-react";
import { useEffect, useState } from "react";

import GoogleAnalytics from "@/components/GoogleAnalytics";
import PlatformAnalyticsTracker from "@/components/PlatformAnalyticsTracker";

type CookieConsentManagerProps = {
  measurementId: string;
};

type CookieConsentValue =
  | "essential"
  | "all";

const STORAGE_KEY =
  "fornecedor_cookie_consent_v1";

function isValidConsent(
  value: string | null,
): value is CookieConsentValue {
  return (
    value === "essential" ||
    value === "all"
  );
}

export default function CookieConsentManager({
  measurementId,
}: CookieConsentManagerProps) {
  const [consent, setConsent] =
    useState<CookieConsentValue | null>(null);

  const [isBannerOpen, setIsBannerOpen] =
    useState(false);

  const [isVisible, setIsVisible] =
    useState(false);

  useEffect(() => {
    const savedConsent =
      window.localStorage.getItem(
        STORAGE_KEY,
      );

    if (isValidConsent(savedConsent)) {
      setConsent(savedConsent);
      return;
    }

    setIsBannerOpen(true);

    const animationFrame =
      window.requestAnimationFrame(() => {
        setIsVisible(true);
      });

    return () => {
      window.cancelAnimationFrame(
        animationFrame,
      );
    };
  }, []);

  function saveConsent(
    value: CookieConsentValue,
  ) {
    window.localStorage.setItem(
      STORAGE_KEY,
      value,
    );

    setConsent(value);
    setIsVisible(false);

    window.setTimeout(() => {
      setIsBannerOpen(false);
    }, 250);
  }

  return (
    <>
      {consent === "all" && (
        <PlatformAnalyticsTracker />
      )}

      {consent === "all" &&
        measurementId && (
          <GoogleAnalytics
            measurementId={measurementId}
          />
        )}

      {isBannerOpen && (
        <div
          className={[
            "pointer-events-none fixed inset-x-0 bottom-0 flex justify-center",
            "z-[100] w-full",
            "px-4 pb-4 sm:px-6 sm:pb-6",
            "transition-all duration-300 ease-out",
            isVisible
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0",
          ].join(" ")}
        >
          <section
            role="dialog"
            aria-label="Preferências de cookies"
            aria-live="polite"
            className="pointer-events-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_22px_65px_rgba(15,23,42,0.22)] sm:p-5"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <Cookie size={18} />
              </div>

              <div>
                <h2 className="text-sm font-bold text-slate-950">
                  Cookies
                </h2>

                <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                  Utilizamos cookies essenciais
                  para manter o funcionamento da
                  plataforma e cookies analíticos
                  para entender como o
                  Fornecedor.Net é utilizado.{" "}
                  <Link
                    href="/politica-de-privacidade"
                    className="font-bold text-blue-600 transition hover:text-blue-700"
                  >
                    Saiba mais
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() =>
                  saveConsent("essential")
                }
                className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-5 text-sm font-bold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
              >
                Apenas essenciais
              </button>

              <button
                type="button"
                onClick={() =>
                  saveConsent("all")
                }
                className="inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-5 text-sm font-bold text-white shadow-[0_8px_20px_rgba(37,99,235,0.22)] transition hover:bg-blue-700"
              >
                Aceitar todos
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
